import { useState, useEffect, useCallback, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import {
  ComputerDesktopIcon,
  CalendarDaysIcon,
  TicketIcon,
  UserPlusIcon,
  WrenchScrewdriverIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import ModalScrollContainer from '@/components/Common/ModalScrollContainer';
import { useAuthStore } from '@/stores/authStore';
import { employeeTargetFor } from '@/config/employeeExperience';
import { hasPermFor } from '@/utils/permissions';

// ---------------------------------------------------------------------------
// Quick actions config
// ---------------------------------------------------------------------------

interface QuickAction {
  id: string;
  label: string;
  description: string;
  icon: React.ElementType;
  iconColor: string;
  iconBg: string;
  /** Where the tile navigates. Omitted when the tile opens something here. */
  path?: string;
  /** Opens the global search dialog instead of navigating. */
  opensSearch?: boolean;
  /** The permission the destination's action needs on the server. A tile
   *  whose action the reader cannot take is not offered (Employee B2): an
   *  employee was offered "Create WO", which ends in a refusal. Omitted for a
   *  tile anyone signed in can use. */
  permission?: string;
}

// Every `?action=` below is now read by the page it points at. Until this
// commit only /spaces?action=book-desk was, so four of the six tiles navigated
// and then did nothing at all — the palette looked functional and was mostly
// decorative.
const QUICK_ACTIONS: QuickAction[] = [
  {
    id: 'book-desk',
    permission: 'booking:create',
    label: 'Book Desk',
    description: 'Reserve a hot desk for today',
    icon: ComputerDesktopIcon,
    iconColor: 'text-blue-600',
    iconBg: 'bg-blue-50',
    path: '/spaces?action=book-desk',
  },
  {
    id: 'book-room',
    permission: 'booking:create',
    label: 'Book Room',
    description: 'Reserve a meeting room',
    icon: CalendarDaysIcon,
    iconColor: 'text-purple-600',
    iconBg: 'bg-purple-50',
    // Not ?action=book-room. There is no "book any room" dialog to open —
    // RoomBookingModal needs a room chosen first, and every room in the
    // directory carries its own Book button. So this lands on that list rather
    // than being wired to a modal that cannot exist.
    path: '/spaces#room-booking',
  },
  {
    id: 'raise-ticket',
    permission: 'helpdesk:create',
    label: 'Raise Ticket',
    description: 'Report a facility issue',
    icon: TicketIcon,
    iconColor: 'text-orn-700',
    iconBg: 'bg-orn-50',
    path: '/helpdesk?action=new',
  },
  {
    id: 'register-visitor',
    permission: 'visitor:create',
    label: 'Pre-register visitor',
    description: 'Pre-register an upcoming visitor',
    icon: UserPlusIcon,
    iconColor: 'text-teal-600',
    iconBg: 'bg-teal-50',
    path: '/visitors?action=new',
  },
  {
    id: 'create-wo',
    permission: 'maintenance:create',
    label: 'Create WO',
    description: 'Create a new work order',
    icon: WrenchScrewdriverIcon,
    iconColor: 'text-yellow-600',
    iconBg: 'bg-yellow-50',
    path: '/maintenance?action=new',
  },
  {
    id: 'search',
    label: 'Search',
    description: 'Search across all modules',
    icon: MagnifyingGlassIcon,
    iconColor: 'text-content-tertiary',
    iconBg: 'bg-surface-muted',
    // Was path: '/dashboard' — a tile labelled "Search across all modules"
    // that navigated to the dashboard and searched nothing. The global search
    // dialog it describes already exists in AppLayout on Cmd+K.
    opensSearch: true,
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface QuickActionsProps {
  /** Opens the global search dialog, which AppLayout owns. */
  onOpenSearch?: () => void;
}

export default function QuickActions({ onOpenSearch }: QuickActionsProps) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const user = useAuthStore((st) => st.user);
  const actions = QUICK_ACTIONS.filter((a) => !a.permission || hasPermFor(user, a.permission));

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Only toggle if not typing in an input/textarea
      const target = e.target as HTMLElement;
      const isInput =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable;

      if (e.key === 'q' && !isInput && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }

      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    },
    [isOpen],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleAction = (action: QuickAction) => {
    setIsOpen(false);
    if (action.opensSearch) {
      onOpenSearch?.();
      return;
    }
    if (action.path) navigate(employeeTargetFor(user, action.path));
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[60]" onClose={() => setIsOpen(false)}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-scrim/40 backdrop-blur-sm" />
        </Transition.Child>

        {/* Panel */}
        <ModalScrollContainer>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-lg rounded-2xl bg-surface p-6 shadow-2xl">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <Dialog.Title className="text-lg font-semibold text-content">
                    Quick Actions
                  </Dialog.Title>
                  <p className="text-xs text-content-subtle mt-0.5">
                    Press <kbd className="rounded bg-surface-muted px-1.5 py-0.5 text-xs font-mono">Q</kbd> to
                    toggle, <kbd className="rounded bg-surface-muted px-1.5 py-0.5 text-xs font-mono">ESC</kbd> to
                    close
                  </p>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="rounded-lg p-1.5 text-content-subtle hover:bg-surface-muted transition-colors"
                 aria-label="Close quick actions">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {actions.map((action) => {
                  const Icon = action.icon;
                  return (
                    <button
                      key={action.id}
                      onClick={() => handleAction(action)}
                      className="flex flex-col items-center gap-2.5 rounded-xl border border-line p-4 text-center transition-all hover:border-brand-300 hover:bg-brand-50 hover:shadow-sm active:scale-[0.98]">
                      <div
                        className={`flex h-12 w-12 items-center justify-center rounded-xl ${action.iconBg}`}
                      >
                        <Icon className={`h-6 w-6 ${action.iconColor}`} />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-content">{action.label}</p>
                        <p className="mt-0.5 text-[10px] text-content-subtle leading-tight">
                          {action.description}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </ModalScrollContainer>
      </Dialog>
    </Transition>
  );
}
